import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DraggableModal from "../DraggableModal";
import EmailDialog from "../EmailDialog";
import { htmlToPdfBlob, safeFilename } from "../../pdfRenderer";
import { publicUrlFor } from "../../storageImages";
import { loadLS, escHtml, reportStyles, reportHeader, reportFooter, openPreviewWindow, downloadBlob } from "./auditCommon";
import { loadFromSupabase } from "../../supabaseDB";
import { appendDisclosureByAccount } from "../emailHelpers";

/* ============================================================================
 * Organic Labels Report
 *
 * Photo sheet of every organic SKU's label, on one page (or a few
 * pages when there are many). The auditor uses this to verify that
 * the on-pack labels match the organic claims on the packing list.
 *
 * Labels come from product.labelPhotoPath (Supabase Storage,
 * `product-labels` bucket) — added by the round-5 → round-6 storage
 * migration. Legacy records with a data: URL still on the row are
 * supported for backward compat; the IndexedDB photo map is also
 * checked as a third tier.
 *
 * Each photo card shows: SKU, description, category, label name.
 * ========================================================================= */

const T = {
  en: {
    title: "Organic Labels",
    sub: "Photo sheet of label artwork",
    selectionTitle: "Pick the products to include",
    selectAll: "Select all", deselectAll: "Deselect all",
    col_sku: "SKU", col_product: "Product", col_label: "Label", col_pack: "Pack",
    btnPreview: "Preview", btnPdf: "Print / PDF", btnEmail: "Email", close: "Close",
    sending: "Generating PDF...",
    emailSubject: "Organic Labels — Photo Sheet",
    empty: "No organic products on file.",
    noLabel: "No label photo on file",
    selectedCount: "selected",
    organic: "Organic",
  },
  es: {
    title: "Etiquetas Orgánicas",
    sub: "Hoja de fotos del arte de etiqueta",
    selectionTitle: "Selecciona los productos a incluir",
    selectAll: "Seleccionar todo", deselectAll: "Deseleccionar todo",
    col_sku: "SKU", col_product: "Producto", col_label: "Etiqueta", col_pack: "Empaque",
    btnPreview: "Vista previa", btnPdf: "Imprimir / PDF", btnEmail: "Enviar Correo", close: "Cerrar",
    sending: "Generando PDF...",
    emailSubject: "Etiquetas Orgánicas — Hoja de Fotos",
    empty: "Sin productos orgánicos en el catálogo.",
    noLabel: "Sin foto de etiqueta",
    selectedCount: "seleccionados",
    organic: "Orgánico",
  },
};

const btnPrimary = { padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 };
const btnSecondary = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 };

/* Resolve a product's label image src.
   Source priority (revised per audit-fix request 6):
     1. product.labelPhotoPath → resolved via publicUrlFor on the
        `product-labels` bucket. Set by the Storage migration that
        runs on product save.
     2. product.labelPhoto when it's already a Storage / HTTP(S) URL.
        The migration sometimes leaves the legacy field with the
        full public URL populated; treat that as a valid source.
     3. product.labelPhoto as an inline `data:` URL. Pre-migration
        records still carry the photo this way; falling back here
        means audits run before the migration finishes still see
        the label, instead of a "no label" placeholder.
   The earlier "Storage path only, no fallbacks" rule made
   pre-migration audits look bad without any user-visible cause.
   A diagnostic console.log narrates which branch matched so any
   future blank-label complaint resolves with one devtools peek. */
function resolveLabelSrc(product) {
  if (!product) return "";
  if (product.labelPhotoPath) {
    var url = publicUrlFor("product-labels", product.labelPhotoPath);
    if (typeof console !== "undefined" && console.log) {
      console.log("[organic-labels] resolved via labelPhotoPath", { sku: product.sku, path: product.labelPhotoPath });
    }
    return url;
  }
  var lp = product.labelPhoto;
  if (typeof lp === "string" && lp) {
    if (lp.startsWith("http://") || lp.startsWith("https://")) {
      if (typeof console !== "undefined" && console.log) {
        console.log("[organic-labels] resolved via labelPhoto URL", { sku: product.sku, url: lp.slice(0, 80) + (lp.length > 80 ? "…" : "") });
      }
      return lp;
    }
    if (lp.startsWith("data:")) {
      if (typeof console !== "undefined" && console.log) {
        console.log("[organic-labels] resolved via labelPhoto data URL", { sku: product.sku, length: lp.length });
      }
      return lp;
    }
  }
  if (typeof console !== "undefined" && console.log) {
    console.log("[organic-labels] no label source for product", {
      sku: product.sku,
      productCode: product.productCode,
      labelPhotoPath: product.labelPhotoPath,
      labelPhotoType: typeof product.labelPhoto,
      labelPhotoLen: typeof product.labelPhoto === "string" ? product.labelPhoto.length : null,
    });
  }
  return "";
}

function labelText(p) {
  return p && (p.label || p.labelInitials) || "";
}
function packText(p) {
  if (!p) return "";
  /* Examples: "12 / 6 oz", "8 / 1 lb" */
  var u = p.unitsPerCase || "";
  var sz = p.size || "";
  if (u && sz) return u + " / " + sz;
  return p.packType || p.description || "";
}

function buildLabelsHTML(products, audit, lang) {
  var t = T[lang === "es" ? "es" : "en"];
  var h = '';
  h += '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(t.title) + '</title>';
  h += '<style>' + reportStyles();
  h += ' .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:10px}';
  h += ' .card{border:1px solid #d1d5db;border-radius:8px;padding:8px;text-align:center;page-break-inside:avoid;display:flex;flex-direction:column}';
  h += ' .card .photo{height:160px;background:#fafafa;border-radius:6px;display:flex;align-items:center;justify-content:center;margin-bottom:6px;overflow:hidden}';
  h += ' .card .photo img{max-width:100%;max-height:100%;object-fit:contain}';
  h += ' .card .none{font-size:9px;color:#999;font-style:italic}';
  h += ' .card .sku{font-size:10px;font-family:"JetBrains Mono",monospace;font-weight:700;color:#1B3A4B;margin-bottom:2px;letter-spacing:0.5px}';
  h += ' .card .desc{font-size:10px;color:#1a1a1a;line-height:1.3}';
  h += ' .card .meta{font-size:8.5px;color:#666;margin-top:3px;text-transform:uppercase;letter-spacing:0.4px}';
  h += '</style></head><body><div class="page">';
  h += reportHeader(t.title, "", "", audit);

  if (products.length === 0) {
    h += '<div style="padding:24px;text-align:center;color:#888;font-style:italic;border:1px dashed #d1d5db;border-radius:4px">' + escHtml(t.empty) + '</div>';
  } else {
    h += '<div class="grid">';
    products.forEach(function (p) {
      var src = resolveLabelSrc(p);
      h += '<div class="card">';
      h += '<div class="photo">' + (src ? '<img src="' + escHtml(src) + '" />' : '<span class="none">' + escHtml(t.noLabel) + '</span>') + '</div>';
      h += '<div class="sku">' + escHtml(p.sku || p.productCode || "—") + '</div>';
      h += '<div class="desc">' + escHtml(p.description || p.productName || p.product || "—") + '</div>';
      var meta = [t.organic, packText(p), labelText(p)].filter(Boolean).join(" · ");
      if (meta) h += '<div class="meta">' + escHtml(meta) + '</div>';
      h += '</div>';
    });
    h += '</div>';
  }
  h += reportFooter(audit, lang);
  h += '</div></body></html>';
  return h;
}

export default function OrganicLabelsPanel({ lang, audit, onClose, onLogReport, initialParams }) {
  const t = T[lang === "es" ? "es" : "en"];
  /* Source of truth = Supabase `products` (carries labelPhotoPath
     populated by the round-6 storage migration). LS is used only as
     a transient fallback when the network is unreachable. */
  const [allProducts, setAllProducts] = useState(null); /* null = loading */
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const lastBlobRef = useRef(null);
  const lastFilenameRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    loadFromSupabase("products")
      .catch(function () { return loadLS("gfi-products", []); })
      .then(function (rows) { if (!cancelled) setAllProducts(rows || []); });
    return function () { cancelled = true; };
  }, []);

  /* Organic-only catalog, sorted by SKU. */
  const products = useMemo(() => {
    if (!Array.isArray(allProducts)) return [];
    return allProducts
      .filter(function (p) {
        if (p.isActive === false) return false;
        var c = String(p.category || "").toLowerCase();
        return c === "organic" || c === "og";
      })
      .sort(function (a, b) { return String(a.sku || a.productCode || "").localeCompare(String(b.sku || b.productCode || "")); });
  }, [allProducts]);

  const [selected, setSelected] = useState({});
  useEffect(() => {
    const next = {};
    if (initialParams && Array.isArray(initialParams.selectedProductIds)) {
      initialParams.selectedProductIds.forEach(function (id) { next[id] = true; });
    } else {
      products.forEach(function (p) { next[p.id] = true; });
    }
    setSelected(next);
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const allChecked = products.length > 0 && products.every(function (p) { return selected[p.id]; });
  const setAll = (v) => setSelected(prev => {
    const next = { ...prev };
    products.forEach(function (p) { next[p.id] = v; });
    return next;
  });
  const selectedProducts = useMemo(() => products.filter(function (p) { return selected[p.id]; }), [products, selected]);

  const buildHtml = useCallback(() => buildLabelsHTML(selectedProducts, audit, lang), [selectedProducts, audit, lang]);

  const handlePreview = () => openPreviewWindow(buildHtml());

  const currentParams = useMemo(
    () => ({ selectedProductIds: selectedProducts.map(function (p) { return p.id; }) }),
    [selectedProducts]
  );

  const handlePdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await htmlToPdfBlob(buildHtml(), { orientation: "portrait" });
      const fn = safeFilename("Organic_Labels_" + (audit && audit.name || "")) + ".pdf";
      lastBlobRef.current = blob; lastFilenameRef.current = fn;
      downloadBlob(blob, fn);
      onLogReport({
        type: "organic_labels",
        title: (lang === "es" ? "PDF descargado · " : "PDF downloaded · ") + selectedProducts.length + " SKUs",
        params: currentParams,
        blob: blob, filename: fn, mime: "application/pdf",
      });
    } catch (e) {
      console.error("[OrganicLabels] pdf failed", e);
      window.alert((lang === "es" ? "Error: " : "Error: ") + (e.message || String(e)));
    } finally { setBusy(false); }
  };

  const [emailProfile, setEmailProfile] = useState({});
  useEffect(() => {
    let alive = true;
    loadFromSupabase("company_profile")
      .then(function (rows) {
        if (!alive) return;
        var p = (rows || []).find(function (r) { return String(r.id) === "gfi"; }) || (rows || [])[0] || {};
        setEmailProfile(p);
      })
      .catch(function () {});
    return function () { alive = false; };
  }, []);
  const profile = emailProfile;
  const signatures = (profile && profile.emailSignatures) || [];
  const fromEmail  = (profile && (profile.outboundEmail || profile.email)) || "";
  /* 2026-09-30 — excludes Sweetleaf/automated accounts, same fix as
     OrganicAuditRegistry.js's own emailAccounts. */
  const emailAccounts = ((profile && profile.emailAccounts) || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });

  return (
    <DraggableModal title={t.title + " · " + (audit && audit.name || "")} onClose={onClose} zIndex={10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.selectionTitle}</div>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            {products.length > 1 && (
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: "var(--color-text-muted)", cursor: "pointer", fontWeight: 600 }}>
                <input type="checkbox" checked={allChecked} onChange={e => setAll(e.target.checked)} />
                {allChecked ? t.deselectAll : t.selectAll}
              </label>
            )}
            <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{selectedProducts.length} / {products.length} {t.selectedCount}</div>
          </div>
        </div>

        {allProducts === null ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", border: "1px dashed var(--color-border)", borderRadius: 8 }}>{lang === "es" ? "Cargando productos..." : "Loading products..."}</div>
        ) : products.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", border: "1px dashed var(--color-border)", borderRadius: 8 }}>{t.empty}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {products.map(function (p) {
              const src = resolveLabelSrc(p);
              const checked = !!selected[p.id];
              return (
                <label key={p.id} style={{ display: "flex", flexDirection: "column", border: "1px solid " + (checked ? "var(--color-accent)" : "var(--color-border)"), borderRadius: 8, padding: 8, cursor: "pointer", background: checked ? "rgba(42,122,111,0.04)" : "var(--color-bg-card)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} />
                    <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-muted)" }}>{p.sku || p.productCode || ""}</div>
                  </div>
                  <div style={{ height: 110, background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", marginBottom: 6 }}>
                    {src ? <img src={src} alt={p.description || p.productName || ""} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /> : <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic", padding: 6, textAlign: "center" }}>{t.noLabel}</span>}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text)" }}>{p.description || p.productName || p.product || "—"}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{[t.organic, packText(p), labelText(p)].filter(Boolean).join(" · ")}</div>
                </label>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{busy ? t.sending : ""}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
          <button onClick={handlePreview} style={btnSecondary} disabled={selectedProducts.length === 0}>{t.btnPreview}</button>
          <button onClick={() => setShowEmail(true)} style={btnSecondary} disabled={selectedProducts.length === 0}>{t.btnEmail}</button>
          <button onClick={handlePdf} style={btnPrimary} disabled={busy || selectedProducts.length === 0}>{t.btnPdf}</button>
        </div>
      </div>

      {showEmail && (
        <EmailDialog
          lang={lang}
          title={t.btnEmail}
          recipients={(audit && audit.auditorEmail) ? [{ name: audit.auditorName || "", email: audit.auditorEmail, checked: true }] : []}
          docOptions={[{ key: "report", label: t.title, checked: true }]}
          defaultSubject={t.emailSubject + " — " + (audit && audit.name || "")}
          defaultBody={lang === "es"
            ? "Adjunto la hoja de fotos de etiquetas orgánicas para la auditoría " + (audit && audit.name || "") + "."
            : "Attached is the organic labels photo sheet for audit " + (audit && audit.name || "") + "."}
          fromEmail={fromEmail}
          emailAccounts={emailAccounts}
          signatures={signatures}
          postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, profile, null)}
          buildAttachments={async () => {
            const blob = await htmlToPdfBlob(buildHtml(), { orientation: "portrait" });
            const fn = safeFilename("Organic_Labels_" + (audit && audit.name || "")) + ".pdf";
            lastBlobRef.current = blob; lastFilenameRef.current = fn;
            return [{ filename: fn, blob: blob, mimeType: "application/pdf" }];
          }}
          onSent={() => {
            const to = (audit && audit.auditorEmail) ? [audit.auditorEmail] : [];
            onLogReport({
              type: "organic_labels",
              title: (lang === "es" ? "Enviado por correo · " : "Sent by email · ") + selectedProducts.length + " SKUs",
              recipients: to,
              params: currentParams,
              blob: lastBlobRef.current, filename: lastFilenameRef.current, mime: "application/pdf",
            });
            setShowEmail(false);
          }}
          onClose={() => setShowEmail(false)}
        />
      )}
    </DraggableModal>
  );
}
